import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Bullseye,
  Button,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  LabelGroup,
  Pagination,
  SearchInput,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, MicrochipIcon, PlusCircleIcon, SearchIcon } from '@patternfly/react-icons';
import { ExpandableRowContent, Table, Tbody, Td, Th, ThProps, Thead, Tr } from '@patternfly/react-table';
import type { VerdaInstanceType, VerdaProductLink } from '../../../types/models/Verda';
import type { VerdaInstanceTypesState, VerdaProductLinksState } from '../../../hooks/useAdminVerda';
import { formatCurrencyAmount, formatUnitPrice } from '../../../utils/money';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { VerdaErrorAlert, VerdaLastFetched, VerdaRefreshButton } from './VerdaTabParts';
import VerdaCreateProductsModal, { VerdaCreateProductsRequest } from './VerdaCreateProductsModal';
import {
  VerdaInstanceKind,
  VerdaSortKey,
  filterInstanceTypes,
  gpuLabel,
  instanceKind,
  isConfidential,
  retiredTypeLinks,
  serverlessPriceLabel,
  sortInstanceTypes,
  storageLabel,
} from './verdaInstanceTypes';

// Column positions (0 is the expand toggle, 1 the row checkbox), as PatternFly's sort API addresses
// columns by index.
const SORT_COLUMN_INDEX: Record<VerdaSortKey, number> = { vram: 4, vcpu: 5, ram: 6, on_demand: 8, spot: 9 };
const COLUMN_COUNT = 11;

const INTEGER_FORMATTER = new Intl.NumberFormat('fr-BE');
const formatCount = (value: number | null): string => (value === null ? '—' : INTEGER_FORMATTER.format(value));

/**
 * Admin › Verda › Instance types — Verda's catalog (about 70 types) with per-hour prices in the list
 * currency (the backend default, EUR). Client-side search on code/name/model plus an All/GPU/CPU filter
 * (the whole list arrives in one call); rows keep Verda's order until a column is sorted, and expand to
 * show the descriptive fields, serverless prices and any deploy warning. The read is owned by the page
 * (shared with the Availability tab, which joins its rows to this catalog).
 *
 * Staff can also turn instance types into hourly catalog products: tick unlinked rows and "Create
 * products", or "Create all missing". Both go through one confirmation modal and one idempotent
 * `POST /admin/verda/products`; the Product column reads the page's shared links
 * (`GET /admin/verda/products`), which are refetched after each create.
 */
type VerdaInstanceTypesTabProps = VerdaInstanceTypesState & { productLinks: VerdaProductLinksState };

const VerdaInstanceTypesTab: React.FunctionComponent<VerdaInstanceTypesTabProps> = ({
  instanceTypes,
  currency,
  fetchedAt,
  loading,
  error,
  refetch,
  productLinks,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kind, setKind] = useState<VerdaInstanceKind>('all');
  const [sortKey, setSortKey] = useState<VerdaSortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  // Instance-type codes ticked for product creation; kept across pages, filters and sorts.
  const [selectedCodes, setSelectedCodes] = useState<ReadonlySet<string>>(() => new Set());
  const [createRequest, setCreateRequest] = useState<VerdaCreateProductsRequest | null>(null);
  const [createRun, setCreateRun] = useState(0);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filtered = useMemo(
    () => filterInstanceTypes(instanceTypes ?? [], debouncedSearch, kind),
    [instanceTypes, debouncedSearch, kind],
  );
  const sorted = useMemo(() => sortInstanceTypes(filtered, sortKey, sortDirection), [filtered, sortKey, sortDirection]);
  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return sorted.slice(start, start + perPage);
  }, [sorted, page, perPage]);

  // Links keyed by instance-type code. `null` while they load or when they failed: selection and the
  // create actions stay off then, since "not linked yet" can't be told apart from "unknown".
  const linkByCode = useMemo(
    () =>
      productLinks.links === null
        ? null
        : new Map<string, VerdaProductLink>(productLinks.links.map((link) => [link.instance_type, link])),
    [productLinks.links],
  );
  const missingCodes = useMemo(
    () =>
      linkByCode === null || instanceTypes === null
        ? []
        : instanceTypes.filter((t) => !linkByCode.has(t.instance_type)).map((t) => t.instance_type),
    [linkByCode, instanceTypes],
  );
  // Products linked to a type Verda has since retired: no catalog row shows them, so they get a note.
  const retiredLinks = useMemo(
    () =>
      productLinks.links === null || instanceTypes === null ? [] : retiredTypeLinks(productLinks.links, instanceTypes),
    [productLinks.links, instanceTypes],
  );
  // Only still-unlinked codes count (a code linked since it was ticked drops out).
  const selectedMissing = useMemo(
    () => missingCodes.filter((code) => selectedCodes.has(code)),
    [missingCodes, selectedCodes],
  );

  const toggleSelected = (code: string, isSelecting: boolean) =>
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (isSelecting) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });

  const openCreate = (request: VerdaCreateProductsRequest) => {
    setCreateRun((run) => run + 1);
    setCreateRequest(request);
  };

  const onProductsCreated = () => {
    setSelectedCodes(new Set());
    void productLinks.refetch();
  };

  const refreshAll = async () => {
    await Promise.all([refetch(), productLinks.refetch()]);
  };

  const selectKind = (next: VerdaInstanceKind) => {
    setKind(next);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setKind('all');
    setPage(1);
  };

  const sortParams = (key: VerdaSortKey): ThProps['sort'] => ({
    sortBy: {
      index: sortKey !== null ? SORT_COLUMN_INDEX[sortKey] : undefined,
      direction: sortDirection,
      defaultDirection: 'asc',
    },
    onSort: (_event, _index, direction) => {
      setSortKey(key);
      setSortDirection(direction);
      setPage(1);
    },
    columnIndex: SORT_COLUMN_INDEX[key],
  });

  const isExpanded = (type: VerdaInstanceType) => expandedIds.includes(type.id);
  const toggleExpanded = (type: VerdaInstanceType) =>
    setExpandedIds((prev) => (prev.includes(type.id) ? prev.filter((id) => id !== type.id) : [...prev, type.id]));

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderTypeCell = (type: VerdaInstanceType) => {
    const cpuName = instanceKind(type) === 'cpu' ? [type.name, type.display_name].filter(Boolean).join(' · ') : '';
    return (
      <>
        <span className="pf-v6-u-font-family-monospace">{type.instance_type}</span>
        {type.deploy_warning !== null && (
          <Tooltip content={type.deploy_warning}>
            <span tabIndex={0} role="img" aria-label="Has a deploy warning" className="pf-v6-u-ml-sm">
              <Icon status="warning" isInline>
                <ExclamationTriangleIcon />
              </Icon>
            </span>
          </Tooltip>
        )}
        {isConfidential(type) && (
          <Label isCompact color="purple" className="pf-v6-u-ml-sm">
            Confidential
          </Label>
        )}
        {cpuName && (
          <div>
            <Content component="small">{cpuName}</Content>
          </div>
        )}
      </>
    );
  };

  const renderProductCell = (type: VerdaInstanceType) => {
    if (productLinks.error !== null) {
      return 'Unknown';
    }
    if (linkByCode === null) {
      return <Spinner size="sm" aria-label="Loading the product link" />;
    }
    const link = linkByCode.get(type.instance_type);
    if (!link) {
      return '—';
    }
    // Linked, but the product row is gone (never in practice: the FK is RESTRICT).
    if (link.product === null) {
      return 'Product missing';
    }
    return (
      <>
        <span className="pf-v6-u-font-family-monospace">{link.product.product_key}</span>
        <div>
          <Content component="small">
            {link.product.default_unit_price !== null
              ? `${formatUnitPrice(link.product.default_unit_price)} / h`
              : 'No default price'}
          </Content>
        </div>
      </>
    );
  };

  const renderProductDetails = (link: VerdaProductLink) => {
    const { product } = link;
    return (
      <>
        <Content component="h4" className="pf-v6-u-mt-md">
          Catalog product
        </Content>
        {product === null && <Content component="p">The linked catalog product no longer exists.</Content>}
        <DescriptionList isCompact isHorizontal columnModifier={{ lg: '2Col' }}>
          {product !== null && (
            <>
              <DescriptionListGroup>
                <DescriptionListTerm>Label</DescriptionListTerm>
                <DescriptionListDescription>{product.label}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Product key</DescriptionListTerm>
                <DescriptionListDescription>
                  <span className="pf-v6-u-font-family-monospace">{product.product_key}</span>
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Default price / h</DescriptionListTerm>
                <DescriptionListDescription>
                  {product.default_unit_price !== null ? formatUnitPrice(product.default_unit_price) : 'Not set'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Status</DescriptionListTerm>
                <DescriptionListDescription>
                  {product.active ? 'Active' : 'Inactive'} ·{' '}
                  {product.tenant_visible ? 'Visible to customers' : 'Hidden from customers'}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </>
          )}
          <DescriptionListGroup>
            <DescriptionListTerm>Verda cost / h at creation</DescriptionListTerm>
            <DescriptionListDescription>
              {formatCurrencyAmount(link.cost_per_hour, link.cost_currency)} (markup {link.markup_percent} %)
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Created</DescriptionListTerm>
            <DescriptionListDescription>{formatWallClockDateTime(link.created_at)}</DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </>
    );
  };

  const renderDetails = (type: VerdaInstanceType, listCurrency: string) => (
    <>
      {type.deploy_warning !== null && (
        <Alert variant="warning" isInline isPlain title={type.deploy_warning} className="pf-v6-u-mb-md" />
      )}
      <DescriptionList isCompact isHorizontal columnModifier={{ lg: '2Col' }}>
        <DescriptionListGroup>
          <DescriptionListTerm>Name</DescriptionListTerm>
          <DescriptionListDescription>
            {type.name ?? '—'}
            {type.display_name !== null && ` (${type.display_name})`}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Manufacturer</DescriptionListTerm>
          <DescriptionListDescription>{type.manufacturer ?? '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Model</DescriptionListTerm>
          <DescriptionListDescription>{type.model ?? '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Description</DescriptionListTerm>
          <DescriptionListDescription>{type.description ?? '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>GPU interconnect</DescriptionListTerm>
          <DescriptionListDescription>{type.p2p ?? '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Instance type ID</DescriptionListTerm>
          <DescriptionListDescription>
            <span className="pf-v6-u-font-family-monospace">{type.id}</span>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Serverless / h</DescriptionListTerm>
          <DescriptionListDescription>
            {serverlessPriceLabel(type.prices.serverless_per_hour, listCurrency)}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Serverless spot / h</DescriptionListTerm>
          <DescriptionListDescription>
            {serverlessPriceLabel(type.prices.serverless_spot_per_hour, listCurrency)}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Best for</DescriptionListTerm>
          <DescriptionListDescription>
            {type.best_for.length > 0 ? type.best_for.join(', ') : '—'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Supported OS images</DescriptionListTerm>
          <DescriptionListDescription>
            {type.supported_os.length > 0 ? (
              <LabelGroup isCompact numLabels={6} aria-label={`Supported OS images for ${type.instance_type}`}>
                {type.supported_os.map((os) => (
                  <Label key={os} isCompact>
                    {os}
                  </Label>
                ))}
              </LabelGroup>
            ) : (
              '—'
            )}
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {linkByCode?.has(type.instance_type) && renderProductDetails(linkByCode.get(type.instance_type)!)}
    </>
  );

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="instance types" message={error} />;
    }
    // No data and no error = loading, including the render before the page's lazy fetch starts.
    if (!instanceTypes || currency === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda instance types" />
        </Bullseye>
      );
    }
    if (instanceTypes.length === 0) {
      return (
        <EmptyState titleText="No instance types" icon={MicrochipIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Verda returned no instance types.</EmptyStateBody>
        </EmptyState>
      );
    }
    if (filtered.length === 0) {
      return (
        <EmptyState titleText="No instance types match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Try a different search term or kind, or clear the filters to see every instance type.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearFilters}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    return (
      <>
        {retiredLinks.length > 0 && (
          <Alert
            variant="info"
            isInline
            isExpandable
            toggleAriaLabel="Show the products linked to retired instance types"
            title={`${retiredLinks.length} catalog product${retiredLinks.length === 1 ? ' is' : 's are'} linked to instance types Verda no longer offers`}
            className="pf-v6-u-mb-md"
          >
            <Content component="p">
              Verda has retired these types, so they have no row below. Retire their products in{' '}
              <Link to="/admin/products-and-prices">Products and Prices</Link> if they should no longer be sold.
            </Content>
            <Content component="ul">
              {retiredLinks.map((link) => (
                <Content component="li" key={link.instance_type}>
                  <span className="pf-v6-u-font-family-monospace">{link.instance_type}</span> →{' '}
                  {link.product === null ? (
                    'product missing'
                  ) : (
                    <>
                      <span className="pf-v6-u-font-family-monospace">{link.product.product_key}</span> (
                      {link.product.active ? 'active' : 'inactive'},{' '}
                      {link.product.tenant_visible ? 'visible to customers' : 'hidden from customers'})
                    </>
                  )}
                </Content>
              ))}
            </Content>
          </Alert>
        )}
        {productLinks.error !== null && (
          <Alert
            variant="warning"
            isInline
            title="The product links could not be loaded, so products can't be created right now"
            className="pf-v6-u-mb-md"
          >
            {productLinks.error}
          </Alert>
        )}
        <Table aria-label="Verda instance types" variant="compact" isExpandable>
          <Thead>
            <Tr>
              <Th screenReaderText="Row expansion" />
              <Th screenReaderText="Select for product creation" />
              <Th>Instance type</Th>
              <Th>GPU</Th>
              <Th sort={sortParams('vram')}>VRAM (GB)</Th>
              <Th sort={sortParams('vcpu')}>vCPU</Th>
              <Th sort={sortParams('ram')}>RAM (GB)</Th>
              <Th>Storage</Th>
              <Th sort={sortParams('on_demand')}>On-demand / h</Th>
              <Th sort={sortParams('spot')}>Spot / h</Th>
              <Th>Product · sale / h</Th>
            </Tr>
          </Thead>
          {paginated.map((type, rowIndex) => (
            <Tbody key={type.id} isExpanded={isExpanded(type)}>
              <Tr isContentExpanded={isExpanded(type)}>
                <Td
                  expand={{
                    rowIndex,
                    isExpanded: isExpanded(type),
                    onToggle: () => toggleExpanded(type),
                    expandId: 'verda-instance-type',
                  }}
                />
                {/* A plain Checkbox in PF's check cell rather than `Td select`, whose checkbox is always
                    named "Select row N" — this one says which instance type it selects. */}
                <Td className="pf-v6-c-table__check">
                  <Checkbox
                    id={`verda-select-${type.id}`}
                    aria-label={`Select ${type.instance_type} for product creation`}
                    isChecked={selectedCodes.has(type.instance_type) && linkByCode?.has(type.instance_type) === false}
                    isDisabled={linkByCode === null || linkByCode.has(type.instance_type)}
                    onChange={(_event, checked) => toggleSelected(type.instance_type, checked)}
                  />
                </Td>
                <Td dataLabel="Instance type">{renderTypeCell(type)}</Td>
                <Td dataLabel="GPU">{gpuLabel(type)}</Td>
                <Td dataLabel="VRAM (GB)">{instanceKind(type) === 'cpu' ? '—' : formatCount(type.gpu.memory_gb)}</Td>
                <Td dataLabel="vCPU">{formatCount(type.cpu.cores)}</Td>
                <Td dataLabel="RAM (GB)">{formatCount(type.memory.size_gb)}</Td>
                <Td dataLabel="Storage">{storageLabel(type)}</Td>
                <Td dataLabel="On-demand / h" modifier="nowrap">
                  {formatCurrencyAmount(type.prices.on_demand_per_hour, currency)}
                </Td>
                <Td dataLabel="Spot / h" modifier="nowrap">
                  {formatCurrencyAmount(type.prices.spot_per_hour, currency)}
                </Td>
                <Td dataLabel="Product · sale / h">{renderProductCell(type)}</Td>
              </Tr>
              <Tr isExpanded={isExpanded(type)}>
                <Td />
                <Td />
                <Td dataLabel="Details" colSpan={COLUMN_COUNT - 2}>
                  <ExpandableRowContent>{isExpanded(type) && renderDetails(type, currency)}</ExpandableRowContent>
                </Td>
              </Tr>
            </Tbody>
          ))}
        </Table>
        <Pagination
          itemCount={sorted.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  const hasRows = !error && instanceTypes !== null && instanceTypes.length > 0;

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          {hasRows && (
            <>
              <ToolbarItem>
                <SearchInput
                  placeholder="Search by instance type, name or model..."
                  value={searchValue}
                  onChange={(_event, value) => setSearchValue(value)}
                  onClear={() => setSearchValue('')}
                />
              </ToolbarItem>
              <ToolbarItem>
                <ToggleGroup aria-label="Filter by kind">
                  <ToggleGroupItem text="All" isSelected={kind === 'all'} onChange={() => selectKind('all')} />
                  <ToggleGroupItem text="GPU" isSelected={kind === 'gpu'} onChange={() => selectKind('gpu')} />
                  <ToggleGroupItem text="CPU" isSelected={kind === 'cpu'} onChange={() => selectKind('cpu')} />
                </ToggleGroup>
              </ToolbarItem>
            </>
          )}
          {hasRows && (
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                isDisabled={selectedMissing.length === 0}
                onClick={() => openCreate({ kind: 'selected', codes: selectedMissing })}
              >
                Create products ({selectedMissing.length})
              </Button>{' '}
              <Button
                variant="secondary"
                isDisabled={linkByCode === null || missingCodes.length === 0}
                onClick={() => openCreate({ kind: 'all', missingCodes })}
              >
                Create all missing ({missingCodes.length})
              </Button>
            </ToolbarItem>
          )}
          <ToolbarItem>
            <VerdaRefreshButton
              loading={loading || productLinks.loading}
              onRefresh={refreshAll}
              what="instance types"
            />
          </ToolbarItem>
          {fetchedAt !== null && !error && (
            <ToolbarItem alignSelf="center">
              <VerdaLastFetched fetchedAt={fetchedAt} />
            </ToolbarItem>
          )}
          {hasRows && filtered.length > 0 && (
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={sorted.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
      {createRequest !== null && (
        <VerdaCreateProductsModal
          key={createRun}
          request={createRequest}
          onClose={() => setCreateRequest(null)}
          onCreated={onProductsCreated}
        />
      )}
    </>
  );
};

export default VerdaInstanceTypesTab;
